import type { ViewerRuntimeProfile } from "./viewerConfig";

export interface PostProcessingBudget {
	multisampling: number;
	aoRadius: number;
	aoSamples: number;
	halfRes: boolean;
	hasAO: boolean;
	hasBloom: boolean;
	hasGrade: boolean;
	hasDof: boolean;
	/** Static film grain, merged into the grade (0 = off). */
	grainStrength: number;
	hasChromaticAberration: boolean;
}

export function resolvePostProcessingBudget(
	runtimeProfile: ViewerRuntimeProfile,
	interiorRoomSpanM?: number,
): PostProcessingBudget {
	const isCinematic = runtimeProfile.presentationMode === "cinematic";
	const hasGrade =
		runtimeProfile.tier !== "low" &&
		(runtimeProfile.gradeWarmth > 0.001 ||
			runtimeProfile.gradeContrast > 0.001);
	return {
		// Thin lav slats alias into checker patches in the composer's un-AA'd
		// target; canvas antialiasing does not cover this offscreen buffer.
		multisampling: interiorRoomSpanM !== undefined && runtimeProfile.tier !== "low"
			? Math.max(4, runtimeProfile.postProcessingMultisampling)
			: runtimeProfile.postProcessingMultisampling,
		// Interior contact zones are centimetres, not the exterior's half metre.
		aoRadius: Math.min(
			runtimeProfile.ssaoKernelRadius * 0.055,
			interiorRoomSpanM === undefined
				? Infinity
				: Math.max(0.08, Math.min(0.22, interiorRoomSpanM * 0.08)),
		),
		aoSamples: isCinematic ? (runtimeProfile.tier === "high" ? 16 : 8) : 4,
		halfRes: runtimeProfile.tier === "low" || !isCinematic,
		hasAO: runtimeProfile.ssaoEnabled,
		hasBloom: runtimeProfile.bloomEnabled && isCinematic,
		hasGrade,
		hasDof: runtimeProfile.depthOfFieldEnabled && isCinematic,
		// Lens cues belong to the photographic grade: they never run
		// without it, so `low` can't reach them even via a stray flag.
		grainStrength: hasGrade ? runtimeProfile.filmGrainStrength : 0,
		hasChromaticAberration:
			hasGrade && runtimeProfile.chromaticAberrationEnabled,
	};
}
